/**
 * Word-level differences between two versions of a text, for the AI
 * proofreading preview: what was removed, what was added, the rest as is.
 *
 * Two passes keep it fast on long report texts: paragraphs (lines) are
 * matched first, then only the changed paragraphs are compared word by word.
 * Returns segments [{ type: 'equal' | 'del' | 'ins', text }].
 */

// Words (letters, digits, apostrophes, hyphens), runs of spaces, or single
// punctuation marks: a corrected comma shows as a comma, not a whole word.
const TOKEN = /\s+|[\p{L}\p{N}_'’-]+|[^\s\p{L}\p{N}]/gu;
// Above this many cells, word matching would be slow: show the block as replaced.
const MAX_CELLS = 1_500_000;

const tokens = (text) => text.match(TOKEN) || [];

const splitLines = (text) => text.match(/[^\n]*\n|[^\n]+/g) || [];

/** Longest-common-subsequence alignment of two arrays → ops. */
function lcsOps(a, b) {
  const n = a.length;
  const m = b.length;
  const table = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      table[i][j] = a[i] === b[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { ops.push(['equal', a[i]]); i += 1; j += 1; }
    else if (table[i + 1][j] >= table[i][j + 1]) { ops.push(['del', a[i]]); i += 1; }
    else { ops.push(['ins', b[j]]); j += 1; }
  }
  while (i < n) { ops.push(['del', a[i]]); i += 1; }
  while (j < m) { ops.push(['ins', b[j]]); j += 1; }
  return ops;
}

function wordOps(before, after) {
  const a = tokens(before);
  const b = tokens(after);
  if (a.length * b.length > MAX_CELLS) return [['del', before], ['ins', after]];
  return lcsOps(a, b);
}

function merge(ops) {
  const out = [];
  for (const [type, text] of ops) {
    if (!text) continue;
    const last = out[out.length - 1];
    if (last && last.type === type) last.text += text;
    else out.push({ type, text });
  }
  return out;
}

export function diffText(before = '', after = '') {
  const a = splitLines(before);
  const b = splitLines(after);
  const lineOps = a.length * b.length > MAX_CELLS ? [['del', before], ['ins', after]] : lcsOps(a, b);

  const ops = [];
  let removed = '';
  let added = '';
  const flush = () => {
    if (removed && added) ops.push(...wordOps(removed, added));
    else if (removed) ops.push(['del', removed]);
    else if (added) ops.push(['ins', added]);
    removed = '';
    added = '';
  };
  for (const [type, text] of lineOps) {
    if (type === 'equal') { flush(); ops.push(['equal', text]); }
    else if (type === 'del') removed += text;
    else added += text;
  }
  flush();
  return merge(ops);
}
